import { useEffect } from 'react';

// ============================================================================
// FULL-SCREEN UTILITY SURFACE CONTAINMENT + FOREGROUND OWNERSHIP
//
// The authenticated utility surfaces (Message History, Saved Talks, Shared
// Memories, Usage this period, Signed-in devices) are rendered ON TOP of the
// still-mounted page that opened them, positioned with `fixed; inset: 0`.
//
// 1. CONTAINMENT. `fixed; inset: 0` alone does not contain them on mobile
//    Safari: the page underneath stays scrollable and rubber-bandable, and a
//    fixed element is sized against the small viewport while the visible area
//    grows as the toolbar collapses. Either way a strip at the bottom stops
//    being covered and the page beneath shows through. While one of these
//    surfaces is mounted the document beneath is therefore clipped: there is
//    nothing left to scroll, rubber-band or reveal. The lock is reference
//    counted, because these surfaces stack, and the member's place on the page
//    underneath is restored when the last one releases it.
//
// 2. FOREGROUND OWNERSHIP ACROSS A DOCUMENT NAVIGATION. These surfaces are
//    component state, not routes, so they leave no history entry. When the
//    member leaves the document (a legal page is a real navigation) and comes
//    back, Safari restores the previous document from its back/forward cache
//    exactly as it was — with the surface still in front. The member asked to
//    go back to the page that invoked it, so on a restore the surface
//    dismisses itself and hands the foreground back.
// ============================================================================

const CLASS = 'utility-overlay-open';

let depth = 0;
let restoreScrollY = 0;

export const useFullScreenSurface = (active = true, onDismiss) => {
  useEffect(() => {
    if (!active) return undefined;
    if (depth === 0 && !document.documentElement.classList.contains(CLASS)) {
      restoreScrollY = window.scrollY;
      document.documentElement.classList.add(CLASS);
      document.body.classList.add(CLASS);
    }
    depth += 1;
    return () => {
      depth -= 1;
      if (depth === 0) {
        document.documentElement.classList.remove(CLASS);
        document.body.classList.remove(CLASS);
        // the clipped document has a shorter scrollable extent, so the offset must be put back
        // only once layout has recovered it — otherwise it is clamped and the member is moved
        void document.documentElement.offsetHeight;
        window.scrollTo(0, restoreScrollY);
      }
    };
  }, [active]);

  useEffect(() => {
    if (!active || typeof onDismiss !== 'function') return undefined;
    const onShow = (e) => { if (e.persisted) onDismiss(); };
    window.addEventListener('pageshow', onShow);
    return () => window.removeEventListener('pageshow', onShow);
  }, [active, onDismiss]);
};

export default useFullScreenSurface;
